import { StandardEvents } from '@shopify/events';

class TieredPromo extends HTMLElement {
  connectedCallback() {
    this.#observeEntrance();
    this.#refreshCart();
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleCartUpdate);
    this.observer?.disconnect();
  }

  #observeEntrance() {
    const container = this.querySelector('[data-tiered-container]');
    if (!container || !container.classList.contains('anim-fade-up')) return;

    this.observer = new IntersectionObserver(
      (entries, observer) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );
    this.observer.observe(container);
  }

  #handleCartUpdate = (event) => {
    event.promise
      ?.then(({ cart, detail }) => {
        if (detail?.didError) return;

        const total = Number(cart?.cost?.totalAmount?.amount);
        if (Number.isFinite(total)) {
          this.#render(total);
        } else {
          this.#refreshCart();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[tiered-promo] Cart update failed:', error);
      });
  };

  async #refreshCart() {
    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const cart = await response.json();
      this.#render(Number(cart.total_price) / 100);
    } catch (error) {
      console.warn('[tiered-promo] Cart fetch failed:', error);
      const status = this.querySelector('[data-tiered-status]');
      if (status) status.textContent = 'Agrega productos al carrito para ver tu progreso.';
    }
  }

  #render(cartTotal) {
    const tiers = Array.from(this.querySelectorAll('[data-tiered-reward]'))
      .map((element) => ({
        element,
        label: element.querySelector('[data-tiered-label]'),
        threshold: Number(element.dataset.threshold),
      }))
      .filter((tier) => Number.isFinite(tier.threshold))
      .sort((a, b) => a.threshold - b.threshold);

    if (!tiers.length) return;

    const labels = {
      unlocked: this.dataset.labelUnlocked || 'DESBLOQUEADO',
      next: this.dataset.labelNext || 'SIGUIENTE',
      locked: this.dataset.labelLocked || 'META VIP',
    };
    let nextTier = null;

    for (const tier of tiers) {
      const isUnlocked = cartTotal >= tier.threshold;
      const isNext = !isUnlocked && !nextTier;
      if (isNext) nextTier = tier;

      tier.element.classList.toggle('unlocked', isUnlocked);
      tier.element.classList.toggle('next', isNext);
      tier.element.classList.toggle('locked', !isUnlocked && !isNext);
      if (tier.label) tier.label.textContent = isUnlocked ? labels.unlocked : isNext ? labels.next : labels.locked;
    }

    const finalThreshold = tiers[tiers.length - 1].threshold;
    const percentage = finalThreshold > 0 ? Math.min(100, Math.max(0, (cartTotal / finalThreshold) * 100)) : 0;
    const total = this.querySelector('[data-tiered-total]');
    const status = this.querySelector('[data-tiered-status]');
    const percentageLabel = this.querySelector('[data-tiered-percentage]');
    const bar = this.querySelector('[data-tiered-progress]');

    if (total) total.textContent = this.#formatMoney(cartTotal);
    if (percentageLabel) percentageLabel.textContent = `${Math.floor(percentage)}%`;
    if (bar) bar.style.width = `${percentage}%`;

    if (status) {
      status.textContent = nextTier
        ? `¡Faltan ${this.#formatMoney(nextTier.threshold - cartTotal)} para tu siguiente regalo!`
        : '¡Felicidades! Has desbloqueado todos los regalos.';
    }
  }

  #formatMoney(amount) {
    const value = Number(amount);
    const formatted = value.toLocaleString('es-MX', {
      minimumFractionDigits: Number.isInteger(value) ? 0 : 2,
      maximumFractionDigits: 2,
    });
    return `$${formatted} MXN`;
  }
}

if (!customElements.get('tiered-promo')) {
  customElements.define('tiered-promo', TieredPromo);
}
